import * as React from 'react';

import {
  calculateChartHeightTarget,
  easeChartHeight,
  getScreenSize,
  ScreenSize,
} from '../utils/perDishCalculations';

interface UseStableChartHeightOptions {
  dataPointCount: number;
  isRefetching?: boolean;
  smoothFactor?: number;
  headerOffset?: number;
  minContentHeight?: number;
  maxContentHeight?: number;
  minContainerHeight?: number;
}

interface UseStableChartHeightResult {
  containerHeight: number;
  contentHeight: number;
}

const DEFAULT_HEADER_OFFSET = 88;
const MIN_CONTAINER_HEIGHT = 280;

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

export function useStableChartHeight(
  options: UseStableChartHeightOptions,
): UseStableChartHeightResult {
  const {
    dataPointCount,
    smoothFactor = 0.3,
    headerOffset = DEFAULT_HEADER_OFFSET,
    minContentHeight = 200,
    maxContentHeight = 520,
    minContainerHeight,
  } = options;

  const isBrowser = typeof window !== 'undefined';

  const resolvedMinContainerHeight = Math.max(
    MIN_CONTAINER_HEIGHT,
    minContainerHeight ?? MIN_CONTAINER_HEIGHT,
  );

  const [screenSize, setScreenSize] = React.useState<ScreenSize>(() => {
    return getScreenSize();
  });

  React.useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const handleResize = () => {
      setScreenSize(getScreenSize());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isBrowser]);

  const baseHeight = React.useMemo(() => {
    const dynamicHeight = calculateChartHeightTarget({
      dataPointCount,
      screenSize,
    });

    return Math.max(resolvedMinContainerHeight, Math.round(dynamicHeight));
  }, [dataPointCount, resolvedMinContainerHeight, screenSize]);

  const desiredHeight = React.useMemo(() => {
    return Math.max(baseHeight, resolvedMinContainerHeight);
  }, [baseHeight, resolvedMinContainerHeight]);

  const [containerHeight, setContainerHeight] =
    React.useState<number>(desiredHeight);

  React.useEffect(() => {
    if (!isBrowser) {
      setContainerHeight(desiredHeight);
      return;
    }

    if (smoothFactor <= 0) {
      setContainerHeight(desiredHeight);
      return;
    }

    let frameId: number | null = null;

    const animate = () => {
      setContainerHeight((current) => {
        if (!Number.isFinite(current)) {
          return desiredHeight;
        }

        const next = easeChartHeight(current, desiredHeight, smoothFactor);
        const distance = Math.abs(next - desiredHeight);

        if (distance <= 1) {
          frameId = null;
          return desiredHeight;
        }

        frameId = window.requestAnimationFrame(animate);
        return next;
      });
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [desiredHeight, isBrowser, smoothFactor]);

  const contentHeight = React.useMemo(() => {
    const rawContentHeight = containerHeight - headerOffset;
    const constrained = clamp(
      rawContentHeight,
      minContentHeight,
      maxContentHeight,
    );
    return Math.round(constrained);
  }, [containerHeight, headerOffset, maxContentHeight, minContentHeight]);

  return {
    containerHeight: Math.round(containerHeight),
    contentHeight,
  };
}
